import { apiSlice } from './apiSlice'

const QUOTES_URL = '/api/quotes'
const ORDERS_URL = '/api/orders'

// Build a public (guest) PDF URL you can open in a new tab if you want
export const publicQuotePdfUrl = (number) =>
  number ? `/api/quotes/by-number/${encodeURIComponent(number)}/pdf/public` : ''

// Reusable response handler to ensure we throw readable errors and return a Blob
async function blobResponseOrThrow(response) {
  if (!response.ok) {
    let msg = `HTTP ${response.status}`
    try {
      const text = await response.text()
      msg = JSON.parse(text)?.message || text || msg
    } catch {}
    // eslint-disable-next-line no-throw-literal
    throw { status: response.status, data: { message: msg } }
  }
  return await response.blob()
}

export const quotesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ---------- Admin CRUD ----------
    adminListQuotes: builder.query({
      query: ({
        page = 1,
        limit = 20,
        status,
        number,
        customer,
        reference,
        from,
        to,
      } = {}) => {
        const params = new URLSearchParams()
        if (page) params.set('page', page)
        if (limit) params.set('limit', limit)
        if (status) params.set('status', status)
        if (number) params.set('number', number)
        if (customer) params.set('customer', customer)
        if (reference) params.set('reference', reference)
        if (from) params.set('from', from)
        if (to) params.set('to', to)
        return `${QUOTES_URL}?${params.toString()}`
      },
      // Ensure each row always has number, reference, referenceShort
     transformResponse: (res) => {
       const normQuotes = (res?.quotes ?? []).map((q) => {
         const number =
           q.number ?? q.quoteNumber ?? ''; // support legacy key
         const reference = q.reference ?? '';
         const referenceShort =
           q.referenceShort ??
           (reference
             ? String(reference)
                 .replace(/[^a-f0-9]/gi, '')
                 .slice(-6)
                 .toUpperCase()
             : '');
         return {
           ...q,
           number,
           reference,
           referenceShort,
         };
       });
       return { ...res, quotes: normQuotes };
     },
      providesTags: (result) =>
       result?.quotes
         ? [
             ...result.quotes.map((q) => ({ type: 'Quote', id: q._id })),
             'Quotes',
           ]
         : ['Quotes'],
    }),

    adminCreateQuote: builder.mutation({
      query: (body = {}) => ({
        url: QUOTES_URL,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Quotes'],
    }),

    adminGetQuote: builder.query({
      query: (id) => `${QUOTES_URL}/${id}`,
      providesTags: (_res, _err, id) => [{ type: 'Quote', id }],
    }),

    adminUpdateQuote: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `${QUOTES_URL}/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_res, _err, arg) => [{ type: 'Quote', id: arg.id }, 'Quotes'],
    }),

    adminDeleteQuote: builder.mutation({
      query: (id) => ({ url: `${QUOTES_URL}/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Quotes'],
    }),

    // ---------- Create/Update a Quote from an Order (returns IDs + URLs) ----------
    generateQuoteForOrder: builder.mutation({
      query: ({ orderId }) => ({
        url: `${QUOTES_URL}/order/${orderId}/generate`,
        method: 'POST',
      }),
      invalidatesTags: ['Quotes'],
    }),

    // ---------- PDF Downloads (all return Blob via .unwrap()) ----------

    // Works even if a Quote doc hasn't been created yet
    downloadQuotePdfByOrder: builder.mutation({
      query: (orderId) => ({
        url: `${ORDERS_URL}/${orderId}/quote.pdf`,
        method: 'GET',
        // IMPORTANT: do not set JSON headers; get binary
        headers: {},
        responseHandler: blobResponseOrThrow,
      }),
    }),

    // By Quote _id
    downloadQuotePdfById: builder.mutation({
      query: (quoteId) => ({
        url: `${QUOTES_URL}/${quoteId}/pdf`,
        method: 'GET',
        headers: {},
        responseHandler: blobResponseOrThrow,
      }),
    }),

    // By human-friendly quote number (private)
    downloadQuotePdfByNumber: builder.mutation({
      query: (number) => ({
        url: `${QUOTES_URL}/by-number/${encodeURIComponent(number)}/pdf`,
        method: 'GET',
        headers: {},
        responseHandler: blobResponseOrThrow,
      }),
    }),

    // Public (guest) by number
    downloadPublicQuotePdfByNumber: builder.mutation({
      query: (number) => ({
        url: `${QUOTES_URL}/by-number/${encodeURIComponent(number)}/pdf/public`,
        method: 'GET',
        headers: {},
        responseHandler: blobResponseOrThrow,
      }),
    }),
    adminConvertQuoteToInvoice: builder.mutation({
      query: ({ id, body }) => ({
        url: `/api/quotes/${id}/convert-to-invoice`,
        method: 'POST',
        body: body || {},
      }),
    }),
    adminSendQuoteEmail: builder.mutation({
      query: (id) => ({
        url: `/api/quotes/${id}/email`,
        method: 'POST',
      }),
    }),
  }),
})

// Hooks
export const {
  useAdminListQuotesQuery,
  useAdminCreateQuoteMutation,
  useAdminGetQuoteQuery,
  useAdminUpdateQuoteMutation,
  useAdminDeleteQuoteMutation,
  useGenerateQuoteForOrderMutation,
  useDownloadQuotePdfByOrderMutation,
  useDownloadQuotePdfByIdMutation,
  useDownloadQuotePdfByNumberMutation,
  useDownloadPublicQuotePdfByNumberMutation,
  useAdminConvertQuoteToInvoiceMutation,
  useAdminSendQuoteEmailMutation,
} = quotesApiSlice

// Optional export
export { QUOTES_URL, ORDERS_URL }
